import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, extractError } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';

/**
 * A Biblioteca do método (etapa 8 do Conselheiro): a formação online do
 * conselheiro e as aulas organizadas pelos 10 temas das 5 Forças, para
 * consultar na preparação de uma manhã. `?tema=N` abre direto num tema (o
 * dossiê de preparação leva para cá).
 */

type AulaDaBiblioteca = {
  id: string;
  tipo: string;
  titulo: string;
  descricao: string | null;
  duracao_seg: number | null;
  concluida: boolean;
  trilha: { slug: string; titulo: string };
  programa: string;
};
type Biblioteca = { temas: { tema: number; nome: string; forca: string; aulas: AulaDaBiblioteca[] }[] };
type Trilha = { id: string; slug: string; titulo: string; descricao?: string | null; programas: { id: string; titulo: string }[] };

const FORCAS = ['Fundação', 'Pessoas', 'Operação', 'Crescimento', 'Futuro'];
const TIPO: Record<string, string> = { VIDEO: 'vídeo', TEXTO: 'texto', AUDIO: 'áudio', PDF: 'PDF', QUIZ: 'quiz' };
const sem = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export function BibliotecaDoMetodoPage() {
  const [params, setParams] = useSearchParams();
  const temaDaUrl = params.get('tema');
  const [forca, setForca] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  const biblioteca = useQuery({
    queryKey: ['biblioteca-do-metodo'],
    queryFn: async () => (await api.get<Biblioteca>('/conteudo/biblioteca')).data,
  });
  const formacao = useQuery({
    queryKey: ['trilhas', 'CONSELHEIRO'],
    queryFn: async () => (await api.get<Trilha[]>('/conteudo/trilhas?publico=CONSELHEIRO')).data,
  });

  // Veio de um tema (o dossiê): abre na força dele e rola até lá.
  useEffect(() => {
    if (temaDaUrl == null || !biblioteca.data) return;
    const t = biblioteca.data.temas.find((x) => String(x.tema) === temaDaUrl);
    if (!t) return;
    setForca(t.forca);
    requestAnimationFrame(() => document.getElementById(`tema-${t.tema}`)?.scrollIntoView({ block: 'start' }));
  }, [temaDaUrl, biblioteca.data]);

  const temas = useMemo(() => {
    const q = sem(busca.trim());
    return (biblioteca.data?.temas ?? [])
      .filter((t) => !forca || t.forca === forca)
      .map((t) => ({
        ...t,
        aulas: q
          ? t.aulas.filter((a) => sem(`${a.titulo} ${a.descricao ?? ''} ${a.programa} ${t.nome}`).includes(q))
          : t.aulas,
      }));
  }, [biblioteca.data, forca, busca]);
  const total = biblioteca.data?.temas.reduce((s, t) => s + t.aulas.length, 0) ?? 0;

  return (
    <PageLayout
      title={NAV_LABELS.bibliotecaDoMetodo}
      description="A formação do conselheiro e o conteúdo do método, organizado pelas 5 Forças e pelos 10 temas — para consultar ao preparar uma manhã."
    >
      <section className="mb-8">
        <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-ha-muted">Formação do conselheiro</h2>
        {formacao.isLoading ? (
          <p className="text-sm text-ha-subtle">Carregando…</p>
        ) : !formacao.data?.length ? (
          <p className="text-sm text-ha-subtle">A trilha de formação ainda não foi publicada.</p>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2">
            {formacao.data.map((t) => (
              <li key={t.id} className="rounded-card border border-ha-border2 bg-ha-card p-5">
                <Link to={`/aprendizado/trilha/${t.slug}`} className="font-semibold text-accent underline">
                  {t.titulo}
                </Link>
                {t.descricao ? <p className="mt-2 text-sm text-ha-subtle">{t.descricao}</p> : null}
                {t.programas.length ? (
                  <p className="mt-2 text-xs text-ha-muted">{t.programas.map((p) => p.titulo).join(' · ')}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-ha-muted">Pelos temas do método</h2>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {[null, ...FORCAS].map((f) => (
            <button
              key={f ?? 'todas'}
              type="button"
              onClick={() => {
                setForca(f);
                if (temaDaUrl) setParams({}, { replace: true });
              }}
              className={`rounded-element border px-3 py-1.5 text-xs font-semibold ${
                forca === f ? 'border-black bg-[var(--primary)] text-black' : 'border-ha-border2 text-ha-subtle'
              }`}
            >
              {f ?? 'Todas as forças'}
            </button>
          ))}
          <input
            className="ml-auto w-full max-w-xs rounded-element border border-ha-border2 bg-transparent px-3 py-1.5 text-sm sm:w-64"
            placeholder="Buscar na biblioteca"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        {biblioteca.isLoading ? (
          <p className="text-sm text-ha-subtle">Carregando…</p>
        ) : biblioteca.isError ? (
          <p className="text-sm text-red-600">{extractError(biblioteca.error).message}</p>
        ) : !total ? (
          <p className="text-sm text-ha-subtle">
            Nenhuma aula ligada aos temas do método ainda. A equipe da Hack marca o tema de cada aula no HAOps.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {temas.map((t) => (
              <article
                key={t.tema}
                id={`tema-${t.tema}`}
                className={`scroll-mt-4 rounded-card border p-4 ${
                  temaDaUrl === String(t.tema) ? 'border-[var(--primary)] border-2' : 'border-ha-border2'
                }`}
              >
                <h3 className="mb-2 flex flex-wrap items-baseline gap-2">
                  <span className="text-base font-semibold text-ha-text">{t.nome}</span>
                  <span className="text-xs text-ha-muted">{t.forca}</span>
                </h3>
                {t.aulas.length ? (
                  <ul className="flex flex-col divide-y divide-ha-border">
                    {t.aulas.map((a) => (
                      <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                        <span className="min-w-0">
                          <Link to={`/aprendizado/aula/${a.id}`} className="text-sm font-medium text-accent underline">
                            {a.titulo}
                          </Link>
                          <span className="block text-xs text-ha-muted">
                            {a.trilha.titulo} · {a.programa}
                          </span>
                          {a.descricao ? <span className="block text-xs text-ha-subtle">{a.descricao}</span> : null}
                        </span>
                        <span className="shrink-0 text-xs text-ha-muted">
                          {TIPO[a.tipo] ?? a.tipo}
                          {a.duracao_seg ? ` · ${Math.max(1, Math.round(a.duracao_seg / 60))} min` : ''}
                          {a.concluida ? ' · ✓ vista' : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-ha-muted">{busca ? 'Nada com essa busca neste tema.' : 'Ainda sem aulas neste tema.'}</p>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </PageLayout>
  );
}
